/**
 * Общий БПФ и оконные функции.
 *
 * Заведён 2026-08-10 как ОБЩИЙ модуль, потому что своя копия Cooley-Tukey
 * лежит в 14 файлах проекта (bpm-detector, key-detector, song-syllables,
 * spectrogram-renderer, clap-mel, forensic-worker, restoration-*, …) — это
 * самая крупная дупликация в аудио-слое. Новый код обязан брать отсюда;
 * старые копии переезжают по мере того, как их файлы всё равно правят.
 *
 * ES-модуль плюс window-глобаль: song-extend импортирует его как модуль,
 * классические скрипты могут взять `window.DspFft`.
 *
 * ⚠️ Здесь ТОЛЬКО степени двойки. Длины вида 2^a·{3,5,15} (их требуют модели
 * MDX-Net: 7680 у Kim_Vocal_2, 5120 у KARA_2) живут в `dsp-fft-mixed.js` —
 * отдельном КЛАССИЧЕСКОМ скрипте (этот файл ES-модуль, а потребитель —
 * `mdx-stem-separator.js` — классический и импортировать его не может). Там же
 * упаковка двух вещественных сигналов в одно комплексное преобразование.
 * Прежде чем писать третью реализацию — посмотри туда.
 */

const twiddleCache = new Map();

/** Предпосчитанные повороты для длины n (степень двойки). */
export function makeTwiddles(n) {
    let t = twiddleCache.get(n);
    if (t) return t;
    const cos = new Float64Array(n >> 1), sin = new Float64Array(n >> 1);
    for (let i = 0; i < n >> 1; i++) {
        const a = (-2 * Math.PI * i) / n;
        cos[i] = Math.cos(a); sin[i] = Math.sin(a);
    }
    t = { cos, sin, n };
    twiddleCache.set(n, t);
    return t;
}

export function hann(n) {
    const w = new Float64Array(n);
    for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
    return w;
}

/** БПФ на месте, radix-2. re/im — типизированные массивы длины 2^k. */
export function fft(re, im, tw) {
    const n = re.length;
    tw = tw || makeTwiddles(n);
    for (let i = 1, j = 0; i < n; i++) {
        let bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) {
            let t = re[i]; re[i] = re[j]; re[j] = t;
            t = im[i]; im[i] = im[j]; im[j] = t;
        }
    }
    for (let len = 2; len <= n; len <<= 1) {
        const half = len >> 1, step = n / len;
        for (let i = 0; i < n; i += len) {
            for (let k = 0, tIdx = 0; k < half; k++, tIdx += step) {
                const cr = tw.cos[tIdx], ci = tw.sin[tIdx];
                const ur = re[i + k], ui = im[i + k];
                const xr = re[i + k + half], xi = im[i + k + half];
                const vr = xr * cr - xi * ci, vi = xr * ci + xi * cr;
                re[i + k] = ur + vr; im[i + k] = ui + vi;
                re[i + k + half] = ur - vr; im[i + k + half] = ui - vi;
            }
        }
    }
}

/**
 * Магнитудный STFT. Возвращает массив Float32Array длины fftSize/2+1 —
 * та же форма, которую ждёт `window.__chromaStream.frame(mag, wts, out)`,
 * чтобы хрому считал уже проверенный код key-detector.js, а не наш.
 *
 * ⚠️ Магнитуда считается через `Math.sqrt(re*re + im*im)`, а НЕ `Math.hypot`.
 * Замер 2026-08-31 (V8, тот же цикл, 30 000 кадров × 513 бинов): hypot
 * 1101–1428 мс против 258–296 мс у sqrt — **в 3.7–5.5 раза**. На настоящем
 * STFT пятиминутного трека (25 832 кадра, hop 128) это 3.2 с против 1.4 с.
 * `Math.hypot` дороже не зря: он защищён от переполнения и потери значимости
 * при возведении в квадрат. Здесь эта защита холостая по ДВУМ причинам, и
 * вторая сильнее первой.
 *
 *   1. После оконного взвешивания |re|,|im| лежат в районе единиц, а
 *      переполнение float64 требует ~1e154.
 *   2. Результат кладётся в Float32Array. Диапазон, где формулы вообще могут
 *      разойтись, лежит ВНЕ представимого во Float32: там, где квадрат
 *      переполняет float64, ответ hypot заведомо больше 3.4e38, то есть тоже
 *      Infinity. ⚠️ Первая версия сторожа сравнивала их на 1e200 как «случай,
 *      который обязан разойтись» — и молча проходил, потому что после
 *      приведения оба Infinity.
 *
 * В самом float64 разница есть — 2.85e-16, порядка eps. После приведения к
 * Float32 на корпусе из 2 647 080 магнитуд (тишина, амплитуда 1e-6, 1.0 и 4.0)
 * НИ ОДНО значение не разошлось. Строгого доказательства «никогда» это не даёт:
 * теоретически разница в 1 ulp float64 может лечь на границу округления
 * Float32. Поэтому проверка живёт в тесте, а не только в этом абзаце —
 * `DspFftMagnitudeTest`.
 *
 * Модуль общий: его STFT считают /chord-finder, оба разделителя стемов,
 * /karaoke, /redub, /video-dubbing, /vocal-remover, /audio-translator и
 * «Продлить песню».
 */
export function stft(x, { fftSize = 1024, hop = 256 } = {}) {
    const win = hann(fftSize), tw = makeTwiddles(fftSize);
    const bins = (fftSize >> 1) + 1;
    const count = Math.max(0, Math.floor((x.length - fftSize) / hop) + 1);
    const re = new Float64Array(fftSize), im = new Float64Array(fftSize);
    const out = new Array(count);
    for (let f = 0; f < count; f++) {
        const off = f * hop;
        for (let i = 0; i < fftSize; i++) { re[i] = x[off + i] * win[i]; im[i] = 0; }
        fft(re, im, tw);
        const mag = new Float32Array(bins);
        for (let b = 0; b < bins; b++) mag[b] = Math.sqrt(re[b] * re[b] + im[b] * im[b]);
        out[f] = mag;
    }
    return { mag: out, hop, fftSize, bins };
}

/**
 * То же самое, но с реальными уступками потоку: длинный трек считается
 * несколько секунд, и без пауз страница замирает — а замерший интерфейс люди
 * читают как «зависло», даже если внутри всё идёт.
 *
 * Пауза — через MessageChannel, а НЕ через `await Promise.resolve()`: промис
 * даёт микрозадачу, она выполняется до отрисовки, и прогресс так и не
 * появится на экране. Проверено на этом проекте не раз.
 */
export async function stftAsync(x, { fftSize = 1024, hop = 256, onProgress = null, chunkFrames = 400 } = {}) {
    const win = hann(fftSize), tw = makeTwiddles(fftSize);
    const bins = (fftSize >> 1) + 1;
    const count = Math.max(0, Math.floor((x.length - fftSize) / hop) + 1);
    const re = new Float64Array(fftSize), im = new Float64Array(fftSize);
    const out = new Array(count);
    for (let f = 0; f < count; f++) {
        const off = f * hop;
        for (let i = 0; i < fftSize; i++) { re[i] = x[off + i] * win[i]; im[i] = 0; }
        fft(re, im, tw);
        const mag = new Float32Array(bins);
        for (let b = 0; b < bins; b++) mag[b] = Math.sqrt(re[b] * re[b] + im[b] * im[b]);
        out[f] = mag;
        if ((f % chunkFrames) === chunkFrames - 1) {
            if (onProgress) onProgress((f + 1) / count);
            await nextTask();
        }
    }
    if (onProgress) onProgress(1);
    return { mag: out, hop, fftSize, bins };
}

/** Уступить поток так, чтобы браузер успел отрисовать кадр. */
export function nextTask() {
    return new Promise((resolve) => {
        if (typeof MessageChannel === 'function') {
            const ch = new MessageChannel();
            ch.port1.onmessage = () => { ch.port1.close(); resolve(); };
            ch.port2.postMessage(0);
        } else {
            setTimeout(resolve, 0);
        }
    });
}

if (typeof window !== 'undefined') window.DspFft = { fft, hann, makeTwiddles, stft, stftAsync, nextTask };
